import { RefreshToken } from '@qogita/canary-types'
import { logError, logWarning } from '@qogita/logging'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useState } from 'react'

import { FOUR_MINUTES_MS } from '#src/constants/time'
import { sessionKey } from '#src/utils/queryKey'
import { FailedToFetchError } from '#utils/errors'

const logContext = {
  event: 'post_auth_refresh_error',
}

export type RefreshDataReturn = {
  refreshToken: RefreshToken | null
  authStatus: 'NOT_SET' | 'INVALID' | 'VERIFIED'
}

const INVALID_RESPONSE = {
  refreshToken: null,
  authStatus: 'INVALID',
} as const

const postAuthRefresh = async () => {
  try {
    const response = await fetch(`/api/auth/refresh/`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    }).catch(() => {
      throw new FailedToFetchError('Failed to fetch refresh token')
    })

    if (response.ok) {
      const refreshToken = (await response.json()) as unknown as RefreshToken
      return { refreshToken, authStatus: 'VERIFIED' } as const
    }

    if (response.status === 401) {
      // Ignore 401 errors because they are expected as
      // we always try to restore the previous session
    } else {
      logError(
        `Failed to refresh token with status ${response.status}`,
        logContext,
      )
    }

    return INVALID_RESPONSE
  } catch (error) {
    /**
     * When error instance is FailedToFetchError
     * Then we don't log
     * So we don't pollute the pager duty as we expect this to happen frequently, on network issues for instance
     */
    if (error instanceof FailedToFetchError) {
      logWarning(error, logContext)
    } else {
      logError(error, logContext)
    }
    return INVALID_RESPONSE
  }
}

export const REFRESH_SESSION_STALE_TIME = FOUR_MINUTES_MS

export const useRefreshSession = () => {
  const [renderCount, setRender] = useState(0)

  const query = useQuery<RefreshDataReturn>({
    queryKey: sessionKey,
    queryFn: () => postAuthRefresh(),
    retry: false,
    staleTime: REFRESH_SESSION_STALE_TIME,
    refetchInterval: REFRESH_SESSION_STALE_TIME,
  })

  /**
   * Fallback data used in initial render. We don't use initialData prop because
   * we want to make sure that the query is always executed on the first render.
   */
  const fallbackData: RefreshDataReturn = {
    authStatus: 'NOT_SET',
    refreshToken: null,
  }

  const queryClient = useQueryClient()

  /**
   * Reset the session data to invalid session, used after logout.
   */
  const invalidate = useCallback(() => {
    const newData: RefreshDataReturn = {
      authStatus: 'INVALID',
      refreshToken: null,
    }
    queryClient.setQueryData(sessionKey, () => newData)
    setRender((previous) => previous + 1) // force rerender because "setQueryData" doesn't trigger it
  }, [queryClient])

  return {
    renderCount, // keep to force rerender after reset
    invalidate,
    ...query,
    data: query.data ?? fallbackData,
  }
}
